import { Component, DoCheck, ElementRef, HostListener, Inject, Input, NgZone, OnDestroy, OnInit, Renderer2, ViewChild } from '@angular/core';
import { DOCUMENT } from '@angular/common';
import { Subscription } from 'rxjs';
import { delay, distinctUntilChanged, filter, finalize, take } from 'rxjs/operators';

import { AppConstants } from '../../app.constants';
import { MonitorKey } from '../../core/models/view-models/monitor-models/monitor-key.type';
import { MonitorHub } from '../../shared-module/hubs/monitor.hub';
import { RemoteControlClickType } from '../../core/enums/remote-control-click-type.enum';
import { ConnectToRemoteMachine } from '../../core/models/remote-control/connect-to-remote-machine.model';
import { ViewMode } from '../../core/enums/view-mode.enum';
import { BehaviorScreen } from '../../core/models/view-models/monitor-models/screen.type';
import { RemoteCursorPointEvent } from '../../core/models/remote-control/remote-cursor-point-event.model';
import { recalculateElementMaxWidthAndHeight } from '../../helpers/recalculate-element-max-width-and-height.helper';
import { ModifierKeyEnum } from '../../core/models/enums/modifier-key.enum';

import { RemoteControlService } from '../../core/services/http-services/remote-control.service';
import { RemoteClickEvent } from '../../core/models/remote-control/remote-click-event.model';
import { UIService } from '../../core/services/utils/ui.service';
import { MonitorChangeViewService } from '../../core/services/http-services/monitor-change-view.service';
import { ScreensStateService } from '../../core/services/screen-state/screens-state.service';

@Component({
  selector: 'monitor-remote-control',
  templateUrl: './monitor-remote-control.component.html',
  styleUrls: ['./monitor-remote-control.component.scss'],
})
export class MonitorRemoteControlComponent implements DoCheck, OnInit, OnDestroy {
  @ViewChild('canvas', { static: true }) canvasElement: ElementRef;
  @Input() monitorKey: MonitorKey = null;
  @Input() remoteControlMonitor: BehaviorScreen;
  public remoteControlClickTypeEnum = RemoteControlClickType;
  public isConnectedToRemoteMachine: boolean;
  private subscription: Subscription = new Subscription();
  private unlisten: Function[] = [];
  private isMouseDown: boolean = false;
  private isCloseRequestSend: boolean;

  constructor(
    private readonly remoteControlService: RemoteControlService,
    private readonly monitorHub: MonitorHub,
    private readonly ngZone: NgZone,
    private readonly renderer: Renderer2,
    private readonly uiService: UIService,
    private readonly monitorChangeViewService: MonitorChangeViewService,
    private readonly screenStateService: ScreensStateService,
    @Inject(DOCUMENT) private document: Document
  ) {
    this.subscription.add(
      this.monitorHub.ReceiveRemoteControlStream.subscribe(
        (base64Img: string) => {
          this.fillInCanvasImage(base64Img);
        }
      )
    );

    this.subscription.add(
      this.uiService.bottomPanelVisibleSubject
        .pipe(delay(1000))
        .subscribe(() =>
          recalculateElementMaxWidthAndHeight(
            ViewMode.FullScreen,
            this.monitorKey.MonitorHandler,
            true
          )
        )
    );
  }

  ngDoCheck(): void {
    setTimeout(() => {
      recalculateElementMaxWidthAndHeight(
        ViewMode.FullScreen,
        this.monitorKey.MonitorHandler
      );
    }, 1000);
  }

  ngOnInit(): void {
    this.subscription.add(
      this.monitorHub.ReceiveRemoteControlScaling
        .pipe(
          delay(1000),
          distinctUntilChanged()
        )
        .subscribe((scale) => {
          if (scale) {
            this.connectToRemoteMachine(scale);
          }
        })
    );

    this.initCanvasEvent(this.canvasElement.nativeElement, 'mousemove', (event: MouseEvent) => {
      const cursorData = new RemoteCursorPointEvent(
        this.isMouseDown,
        event.offsetX,
        event.offsetY
      );
      this.subscription.add(
        this.remoteControlService.setCursorPosition(cursorData, this.monitorKey.MonitorHandler)
          .subscribe()
      );
    });

    this.initCanvasEvent(this.canvasElement.nativeElement, 'mouseover', () => {
      this.isMouseDown = false;
    });

    this.initCanvasEvent(this.canvasElement.nativeElement, 'mouseout', () => {
      this.isMouseDown = true;
    });

    this.initCanvasEvent(this.canvasElement.nativeElement, 'contextmenu', (event: MouseEvent) => {
      event.preventDefault();
      const contextData = new RemoteClickEvent(
        RemoteControlClickType.RightClick,
        event.offsetX,
        event.offsetY
      );
      this.subscription.add(
        this.remoteControlService.clickRemoteDesktop(contextData).subscribe()
      );
    });

    this.initCanvasEvent(this.canvasElement.nativeElement, 'wheel', (event: WheelEvent) => {
      event.preventDefault();

      if (!!Math.abs(event.deltaY)) {
        this.subscription.add(
          this.remoteControlService.scrollRemoteDesktopVertically(Math.sign(-event.deltaY)).subscribe()
        );
      }

      if (!!Math.abs(event.deltaX)) {
        this.subscription.add(
          this.remoteControlService.scrollRemoteDesktopHorizontally(Math.sign(event.deltaX)).subscribe()
        );
      }
    });

    this.monitorHub.remoteControlDisconnected
      .pipe(
        filter(() => !!!this.isCloseRequestSend),
        take(1)
      )
      .subscribe(() =>
        this.closeRemoteControlWindow());

    this.closeBottomPanel();
  }

  ngOnDestroy(): void {
    this.subscription.unsubscribe();
    this.unlisten.forEach((func: Function) => func());
  }

  onCanvasClick(event: PointerEvent): void {
    const clickData = new RemoteClickEvent(
      RemoteControlClickType.Click,
      event.offsetX,
      event.offsetY
    );
    this.subscription.add(
      this.remoteControlService.clickRemoteDesktop(clickData).subscribe()
    );
  }

  // The same as click but it is easy to debug
  onCanvasDoubleClick(event: PointerEvent) {
    const doubleClickData = new RemoteClickEvent(
      RemoteControlClickType.DoubleClick,
      event.offsetX,
      event.offsetY
    );
    this.subscription.add(
      this.remoteControlService.clickRemoteDesktop(doubleClickData).subscribe()
    );
  }

  closeRemoteControlWindow(): void {
    this.isCloseRequestSend = true;
    this.enableMouseCursor();

    this.subscription.add(
      this.remoteControlService.closeRemoteControlSession().subscribe(() => {
        if (this.screenStateService.screensCollectionChanged.value) {
          let currentMonitor = this.screenStateService.screensCollectionChanged.value.find((mon) => {
            return mon.MachinePhysicalAddress === this.monitorKey.MachinePhysicalAddress
              && mon.MonitorHandler === this.monitorKey.MonitorHandler;
          });
          if (!currentMonitor) {
            currentMonitor = this.screenStateService.screensCollectionChanged.value[0];
          }
          this.monitorChangeViewService.initChangeImageModel(currentMonitor, true);
        }
      })
    );
  }

  private connectToRemoteMachine(scale: number): void {
    setTimeout(() => {
      const connectToRemoteMachineData = this.initCanvasWidthAndHeight(scale);
      this.subscription.add(
        this.remoteControlService
          .connectToMachine(connectToRemoteMachineData)
          .pipe(finalize(() => this.isConnectedToRemoteMachine = true))
          .subscribe()
      );
    }, 1500);
  }

  private enableMouseCursor(): void {
    this.renderer.removeClass(document.body, 'cursor-not-allowed');
    this.renderer.removeClass(document.querySelector('app-root'), 'disable-cursor');
    this.renderer.removeClass(document.querySelector('.filters-bar'), 'additionall-padding');
  }

  private closeBottomPanel(): void {
    this.uiService.isBottomPanelButtonHidden = true;
    this.uiService.unpinBottomPanel();
    this.uiService.hideBottomPanel();
  }

  private fillInCanvasImage(base64Img: string): void {
    const ctx = this.getCanvacContext();
    if (ctx) {
      const source = `data:image/png;base64, ${base64Img}`;
      const img = new Image();
      img.src = source;
      img.onload = function () {
        ctx.drawImage(img, 0, 0, ctx.canvas.width, ctx.canvas.height);
      };
    }
  }

  private initCanvasWidthAndHeight(scale: number): ConnectToRemoteMachine {
    const ctx = this.getCanvacContext();
    if (ctx) {
      const canvasWidth =
        (this.document.querySelector('canvas') as HTMLElement).offsetWidth - (AppConstants.REMOTE_CONTROL_MODAL_WINDOW_PADDING / 2);
      const canvasHeight =
        (canvasWidth  * AppConstants.REMOTE_CONTROL_HEIGHT_TO_WIDTH_PROPRTION - AppConstants.REMOTE_CONTROL_MODAL_WINDOW_PADDING);
      ctx.canvas.width = canvasWidth * scale;
      ctx.canvas.height = canvasHeight * scale;
    }

    const connectToRemoteMachine: ConnectToRemoteMachine =
      new ConnectToRemoteMachine(
        this.monitorKey,
        ctx.canvas.height,
        ctx.canvas.width,
        this.remoteControlMonitor.BoundX,
        this.remoteControlMonitor.BoundY
      );
    return connectToRemoteMachine;
  }

  private getCanvacContext(): CanvasRenderingContext2D {
    const canvas = this.canvasElement.nativeElement;
    if (canvas) {
      const ctx: CanvasRenderingContext2D = canvas.getContext('2d');
      return ctx;
    }

    return null;
  }

  private initCanvasEvent(
    element: HTMLElement,
    eventName: string,
    callback: (event: any) => boolean | void
  ): void {
    this.ngZone.runOutsideAngular(() => {
      this.unlisten.push(
        this.renderer.listen(
          element,
          eventName,
          callback
        )
      );
    });
  }

  private getModifierKey(e: KeyboardEvent): number {
    let modifierKey = ModifierKeyEnum.None;
    if (e.shiftKey && e.ctrlKey) {
      modifierKey = ModifierKeyEnum.CtrlShift;
    }
    else if (e.altKey && e.shiftKey) {
      modifierKey = ModifierKeyEnum.AltShift;
    }
    else if (e.shiftKey) {
      modifierKey = ModifierKeyEnum.Shift;
    }
    else if (e.ctrlKey) {
      modifierKey = ModifierKeyEnum.Ctrl;
    }
    else if (e.altKey) {
      modifierKey = ModifierKeyEnum.Alt;
    }
    return modifierKey;
  }

  @HostListener('document:keydown', ['$event'])
  public handleKeyboardEvent(event: KeyboardEvent) {
    event.preventDefault();
    event.stopPropagation();

    if (event.ctrlKey && event.altKey && event.key === AppConstants.Q_EVENT_KEY) {
      this.closeRemoteControlWindow();
      return;
    }

    if (
      event.key === AppConstants.CTRL_EVENT_KEY
      || (event.key === AppConstants.SHIFT_EVENT_KEY
      && this.getModifierKey(event) !== ModifierKeyEnum.AltShift)
    ) {
      return;
    }

    this.subscription.add(
      this.remoteControlService.manageKeyboard(event.keyCode, this.getModifierKey(event)).subscribe()
    );
  }

  @HostListener('window:resize') onResize(): void {
    recalculateElementMaxWidthAndHeight(
      ViewMode.FullScreen,
      this.monitorKey.MonitorHandler,
      true
    );
  }

  @HostListener('mousedown', ['$event']) onMouseDown(): void {
    this.isMouseDown = true;
  }

  @HostListener('mouseup', ['$event']) onMouseUp(): void {
    this.isMouseDown = false;
  }

  @HostListener('window:beforeunload', ['$event']) closeRemoteControlSession() : void {
    this.closeRemoteControlWindow();
  }
}
